import React from "react";
import { Chart as ChartJS } from "chart.js";
import { Radar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(ChartDataLabels);

// Constants
const GREEN_COLOR = "rgba(10, 140, 74, 1)";
const TRANSPARENT_GREEN = "rgba(10, 140, 74, 0)";
const LIGHT_GREEN = "rgba(10, 140, 74, 0.3)";

// Utility functions
const drawArrow = (ctx, centerX, centerY, angle, radius) => {
  const arrowLength = radius * 0.7;
  const arrowStartX = centerX + Math.cos(angle) * radius * 0.88;
  const arrowStartY = centerY + Math.sin(angle) * radius * 0.88;
  const arrowEndX = centerX + Math.cos(angle) * (radius - arrowLength);
  const arrowEndY = centerY + Math.sin(angle) * (radius - arrowLength);

  ctx.beginPath();
  ctx.moveTo(arrowStartX, arrowStartY);
  ctx.lineTo(arrowEndX, arrowEndY);
  ctx.strokeStyle = LIGHT_GREEN;
  ctx.lineWidth = 2;
  ctx.stroke();

  const headLength = 10;
  const angle1 = angle - Math.PI / 6;
  const angle2 = angle + Math.PI / 6;

  ctx.beginPath();
  ctx.moveTo(arrowEndX, arrowEndY);
  ctx.lineTo(
    arrowEndX + headLength * Math.cos(angle1),
    arrowEndY + headLength * Math.sin(angle1)
  );
  ctx.lineTo(
    arrowEndX + headLength * Math.cos(angle2),
    arrowEndY + headLength * Math.sin(angle2)
  );
  ctx.closePath();
  ctx.fillStyle = LIGHT_GREEN;
  ctx.fill();
};

const drawTextAlongArc = (
  ctx,
  str,
  centerX,
  centerY,
  radius,
  angle,
  posStart
) => {
  ctx.font = "bold 16px Arial";
  ctx.fillStyle = GREEN_COLOR;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.save();
  ctx.translate(centerX, centerY);
  ctx.rotate(posStart / 360);
  for (let n = 0; n < str.length; n++) {
    ctx.rotate(angle / str.length);
    ctx.save();
    ctx.translate(0, -1 * radius);
    ctx.fillText(str[n], 0, 0);
    ctx.restore();
  }
  ctx.restore();
};

const drawTextDescArc = (
  ctx,
  str,
  centerX,
  centerY,
  radius,
  angle,
  posStart
) => {
  ctx.font = "10px Arial";
  ctx.fillStyle = "black";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.save();
  ctx.translate(centerX, centerY);
  ctx.rotate(posStart / 360);
  for (let n = 0; n < str.length; n++) {
    ctx.rotate(angle / str.length);
    ctx.save();
    ctx.translate(0, -1 * radius);
    ctx.fillText(str[n], 0, 0);
    ctx.restore();
  }
  ctx.restore();
};

// Plugins
const reverseAxisArrowPlugin = {
  id: "reverseAxisArrow",
  afterDraw: (chart) => {
    const { ctx, chartArea } = chart;
    const centerX = (chartArea.left + chartArea.right) / 2;
    const centerY = (chartArea.top + chartArea.bottom) / 2;
    const radius =
      Math.min(
        chartArea.right - chartArea.left,
        chartArea.bottom - chartArea.top
      ) / 2;

    drawArrow(ctx, centerX, centerY, Math.PI * 0.93, radius); // Z axis
    drawArrow(ctx, centerX, centerY, Math.PI * 1.13, radius); // K axis
  },
};

const options = {
  layout: {
    padding: {
      top: 68,
      right: 68,
      bottom: 68,
      left: 68,
    },
  },
  plugins: {
    tooltip: {
      enabled: false, // Disable tooltips
    },
    legend: {
      display: false,
    },
    datalabels: {
      anchor: "center",
      offset: 0,
      font: {
        size: 10,
        weight: "bold",
      },
      formatter: (value, context) => {
        const label = context.chart.data.labels[context.dataIndex];

        // Reverse the value for labels "Z" and "K"
        if (label === "Z" || label === "K") {
          return 9 - value + 1; // Reverse the value (1 becomes 9, 2 becomes 8, etc.)
        }

        // Return the original value for other labels
        return Math.round(value - 1);
      },
    },
  },
  scales: {
    r: {
      pointLabels: {
        padding: 10, // Increase padding value to push labels outward
        font: {
          size: 14,
          weight: "bold",
        },
      },
      angleLines: {
        display: false,
        color: "rgba(0, 0, 0, 0.1)",
      },
      grid: {
        circular: false, // Disable circular grid lines
        color: "rgba(0, 0, 0, 0)", // Set grid line color to transparent
      },
      suggestedMin: 0,
      suggestedMax: 10,
      ticks: {
        display: false,
        max: 10,
        stepSize: 1,
        font: {
          size: 10, // Adjust font size as needed
          weight: "bold", // Make ticks bold
        },
        color: "rgba(0, 0, 0, 0.7)", // Make ticks darker
        backdropColor: "rgba(255, 255, 255, 0.75)", // Add a semi-transparent white background
        backdropPadding: 2,
        z: 1, // Ensure ticks are drawn on top
      },
    },
  },
};

const externalLabelPlugin = {
  id: "externalLabel",
  afterDraw: (chart) => {
    const { ctx, chartArea } = chart;
    const centerX = (chartArea.left + chartArea.right) / 2;
    const centerY = (chartArea.top + chartArea.bottom) / 2;

    ctx.save();

    // Draw circles
    [265, 305].forEach((radius) => {
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
      ctx.strokeStyle = GREEN_COLOR;
      ctx.lineWidth = 1;
      ctx.stroke();
    });

    // Draw descriptive texts
    const descTexts = [
      {
        text: [
          "organized",
          "type",
          "need to be",
          "noticed",
          "social",
          "need to",
          "finish task",
          "hard",
          "intense worked",
          "need to",
          "achieve",
          "extension",
          "supervision",
          "rules and",
          "need for",
          "making",
          "decision",
          "ease in",
          "pace",
          "authority",
          "support",
          "need to",
          "type",
          "vigorous",
          "need to be",
          "forceful",
          "emotional",
          "restraint",
          "need for",
          "change",
          "leadership",
          "role",
          "need to",
          "control others",
          "interest in",
          "working with",
          "details",
          "type",
          "theoretical",
          "need for",
          "closeness and",
          "affection",
          "need to",
          "belong to",
          "groups",
        ],
        radius: [
          -279, -291, -279, -291, -279, 291, 279, 291, 279, 291, 279, -291, 272,
          284, 296, 272, 284, 296, 284, 272, 284, 296, 279, 291, 291, 279, 291,
          279, 291, 279, 291, 279, 291, 279, -272, -284, -296, -291, -279, -272,
          -284, -296, -272, -284, -296,
        ],
        angle: [
          -Math.PI / 16,
          -Math.PI / 32,
          -Math.PI / 16,
          -Math.PI / 24,
          -Math.PI / 24,
          Math.PI / 18,
          Math.PI / 14,
          Math.PI / 32,
          Math.PI / 12,
          Math.PI / 18,
          Math.PI / 18,
          -Math.PI / 18,
          Math.PI / 14,
          Math.PI / 16,
          Math.PI / 16,
          Math.PI / 18,
          Math.PI / 18,
          Math.PI / 18,
          Math.PI / 32,
          Math.PI / 16,
          Math.PI / 20,
          Math.PI / 20,
          Math.PI / 32,
          Math.PI / 18,
          Math.PI / 16,
          Math.PI / 18,
          Math.PI / 16,
          Math.PI / 18,
          Math.PI / 20,
          Math.PI / 24,
          Math.PI / 14,
          Math.PI / 32,
          Math.PI / 20,
          Math.PI / 12,
          -Math.PI / 14,
          -Math.PI / 14,
          -Math.PI / 24,
          -Math.PI / 32,
          -Math.PI / 14,
          -Math.PI / 18,
          -Math.PI / 12,
          -Math.PI / 18,
          -Math.PI / 18,
          -Math.PI / 16,
          -Math.PI / 20,
        ],
        posStart: [
          375, 360, -295, -305, -200, -152.5, -160, -22, -50, 80, 80, -193,
          -270, -268, -268, 405, 405, 405, 540, -370, -365, -365, 658, 645,
          -472, -470, -600, -595, -720, -715, 182, 200, 295, 280, 270, 268, 255,
          145, 160, 35, 50, 35, -80, -78, -82,
        ],
      },
      // ... (add other text configurations here)
    ];

    descTexts.forEach(({ text, radius, angle, posStart }) => {
      text.forEach((t, i) =>
        drawTextDescArc(
          ctx,
          t,
          centerX,
          centerY,
          radius[i],
          angle[i],
          posStart[i]
        )
      );
    });

    // Draw curved texts
    drawTextAlongArc(
      ctx,
      "Work Direction",
      centerX,
      centerY,
      317,
      Math.PI / 4,
      -150
    );

    drawTextAlongArc(
      ctx,
      "Followership",
      centerX,
      centerY,
      317,
      Math.PI / 6,
      -380
    );

    drawTextAlongArc(
      ctx,
      "Temperament",
      centerX,
      centerY,
      317,
      Math.PI / 3.5,
      -740
    );

    drawTextAlongArc(
      ctx,
      "Leadership",
      centerX,
      centerY,
      317,
      Math.PI / 4,
      180
    );

    drawTextAlongArc(ctx, "Activity", centerX, centerY, 317, Math.PI / 7, 535);

    drawTextAlongArc(
      ctx,
      "Socialnature",
      centerX,
      centerY,
      -317,
      -Math.PI / 3,
      35
    );
    drawTextAlongArc(
      ctx,
      "Workstyle",
      centerX,
      centerY,
      -317,
      -Math.PI / 4,
      380
    );
  },
};

function RadarChart({ chartData }) {
  const processedData = {
    ...chartData,
    datasets: [
      {
        ...chartData.datasets[0],
        data: chartData.datasets[0].data.map((value, index) => {
          if (index === 14 || index === 16) return 9 - value;
          return value + 1;
        }),
        backgroundColor: TRANSPARENT_GREEN,
        pointBorderColor: GREEN_COLOR,
        borderColor: GREEN_COLOR,
        pointBorderWidth: 1,
        borderWidth: 1,
        fill: false,
        pointRadius: 6,
        pointHoverRadius: 6,
      },
      ...Array(9)
        .fill()
        .map((_, i) => ({
          label: `Dataset ${i + 2}`,
          data: Array(20).fill(i + 2),
          backgroundColor: TRANSPARENT_GREEN,
          borderColor: TRANSPARENT_GREEN,
          borderWidth: 0,
          fill: false,
        })),
    ],
  };

  return (
    <div style={{ position: "relative", height: "650px", width: "650px" }}>
      <Radar
        data={processedData}
        options={options}
        plugins={[externalLabelPlugin, reverseAxisArrowPlugin]}
      />
    </div>
  );
}

export default RadarChart;
